import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Space, Typography, Spin, Dropdown, Modal, Form, Input, message, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BarChartOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  KeyOutlined,
  ApiOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
  MessageOutlined,
  PictureOutlined,
  FileImageOutlined,
  ShopOutlined,
  AppstoreOutlined,
  WarningOutlined,
  SettingOutlined,
  TransactionOutlined,
  LineChartOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/stores/auth.store';
import request from '@/utils/request';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuItems: MenuProps['items'] = [
  {
    key: 'overview',
    icon: <DashboardOutlined />,
    label: '概览',
    children: [
      { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
      { key: '/marketplace', icon: <LineChartOutlined />, label: '市场看板' },
      { key: '/analytics', icon: <BarChartOutlined />, label: '数据分析' },
    ],
  },
  {
    key: 'user-management',
    icon: <TeamOutlined />,
    label: '用户管理',
    children: [
      { key: '/users', icon: <UserOutlined />, label: '消费者' },
      { key: '/creators', icon: <ShopOutlined />, label: '创作者' },
      { key: '/feedback', icon: <MessageOutlined />, label: '意见反馈' },
    ],
  },
  {
    key: 'ai-models',
    icon: <DatabaseOutlined />,
    label: 'AI 模型',
    children: [
      { key: '/llm-models', icon: <DatabaseOutlined />, label: 'LLM 模型' },
      { key: '/image-models', icon: <PictureOutlined />, label: '图像模型' },
    ],
  },
  {
    key: 'call-logs',
    icon: <ApiOutlined />,
    label: '调用日志',
    children: [
      { key: '/llm-calls', icon: <ApiOutlined />, label: 'LLM 调用' },
      { key: '/image-gen-logs', icon: <FileImageOutlined />, label: '图像生成' },
    ],
  },
  {
    key: 'commerce',
    icon: <ShopOutlined />,
    label: '交易与运营',
    children: [
      { key: '/bundles', icon: <AppstoreOutlined />, label: '套图审核' },
      { key: '/exceptions', icon: <WarningOutlined />, label: '异常订单' },
      { key: '/withdrawals', icon: <TransactionOutlined />, label: '提现审核' },
      { key: '/marketplace-settings', icon: <SettingOutlined />, label: '市场参数' },
    ],
  },
  {
    key: 'system',
    icon: <ToolOutlined />,
    label: '系统工具',
    children: [
      { key: '/storage-test', icon: <CloudUploadOutlined />, label: '存储测试' },
    ],
  },
];

const routeToGroup: Record<string, string> = {
  '/dashboard': 'overview',
  '/marketplace': 'overview',
  '/analytics': 'overview',
  '/users': 'user-management',
  '/creators': 'user-management',
  '/feedback': 'user-management',
  '/llm-models': 'ai-models',
  '/image-models': 'ai-models',
  '/llm-calls': 'call-logs',
  '/image-gen-logs': 'call-logs',
  '/bundles': 'commerce',
  '/exceptions': 'commerce',
  '/withdrawals': 'commerce',
  '/marketplace-settings': 'commerce',
  '/storage-test': 'system',
};

const AuthLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, admin, logout, checkSession, loading } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const { token: themeToken } = theme.useToken();

  useEffect(() => {
    checkSession().finally(() => setSessionChecked(true));
  }, [checkSession]);

  useEffect(() => {
    if (sessionChecked && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [sessionChecked, isAuthenticated, navigate]);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = async (values: { oldPassword: string; newPassword: string }) => {
    setPasswordLoading(true);
    try {
      await request.post('/admin/change-password', values);
      message.success('Password changed');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (err) {
      message.error((err as Error)?.message || 'Failed');
    } finally {
      setPasswordLoading(false);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: 'password', icon: <KeyOutlined />, label: 'Change Password' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  const onUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'password') setPasswordModalOpen(true);
    if (key === 'logout') handleLogout();
  };

  const activeKey = (() => {
    const path = location.pathname;
    if (path.startsWith('/users')) return '/users';
    if (path.startsWith('/analytics')) return '/analytics';
    if (path.startsWith('/llm-models')) return '/llm-models';
    if (path.startsWith('/llm-calls')) return '/llm-calls';
    if (path.startsWith('/image-models')) return '/image-models';
    if (path.startsWith('/image-gen-logs')) return '/image-gen-logs';
    if (path.startsWith('/storage-test')) return '/storage-test';
    if (path.startsWith('/feedback')) return '/feedback';
    if (path.startsWith('/creators')) return '/creators';
    if (path.startsWith('/bundles')) return '/bundles';
    if (path.startsWith('/exceptions')) return '/exceptions';
    if (path.startsWith('/marketplace-settings')) return '/marketplace-settings';
    if (path.startsWith('/withdrawals')) return '/withdrawals';
    if (path.startsWith('/marketplace')) return '/marketplace';
    return '/dashboard';
  })();

  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const group = routeToGroup[activeKey];
    return group ? [group] : ['overview'];
  });

  useEffect(() => {
    const group = routeToGroup[activeKey];
    if (group) {
      setOpenKeys((prev) => (prev.includes(group) ? prev : [...prev, group]));
    }
  }, [activeKey]);

  if (!sessionChecked || loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="light"
        style={{
          borderRight: `1px solid ${themeToken.colorBorderSecondary}`,
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
          }}
        >
          <Typography.Title
            level={4}
            style={{ margin: 0, color: themeToken.colorPrimary }}
          >
            {collapsed ? 'Z' : 'Zero Admin'}
          </Typography.Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          openKeys={collapsed ? undefined : openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          items={menuItems}
          style={{ border: 'none', marginTop: 8 }}
          onClick={({ key }) => {
            if (key.startsWith('/')) navigate(key);
          }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            background: '#fff',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />
          <Dropdown menu={{ items: userMenuItems, onClick: onUserMenuClick }} placement="bottomRight">
            <Button type="text" icon={<UserOutlined />}>
              {admin?.username ?? 'Admin'}
            </Button>
          </Dropdown>
        </Header>

        {/* Change Password Modal */}
        <Modal
          title="Change Password"
          open={passwordModalOpen}
          onCancel={() => { setPasswordModalOpen(false); passwordForm.resetFields(); }}
          footer={null}
        >
          <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
            <Form.Item name="oldPassword" label="Current Password" rules={[{ required: true, message: 'Required' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="newPassword" label="New Password" rules={[{ required: true, message: 'Required' }, { min: 6, message: 'At least 6 characters' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => { setPasswordModalOpen(false); passwordForm.resetFields(); }}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={passwordLoading}>Confirm</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
        <Content
          style={{
            margin: 24,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AuthLayout;
